import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, Save, CheckCircle2, Plug, Box } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { ModuleTier } from '@/lib/featureFlags';
import { ADDON_DEFINITIONS } from '@/lib/featureFlags';

const TIER_LABELS: Record<ModuleTier, { label: string; color: string }> = {
  basico: { label: 'Básico', color: 'bg-muted text-muted-foreground' },
  profissional: { label: 'Profissional', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  enterprise: { label: 'Enterprise', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' },
};

const ADDON_ICONS: Record<string, React.ElementType> = {
  addon_erp: Plug,
  addon_bim: Box,
};

interface FlagRow {
  id: string;
  cluster_id: string;
  modulo: string;
  tipo_modulo: string;
  ativo: boolean;
  config: Record<string, any> | null;
  cluster_name?: string;
}

export const TenantTierManager: React.FC = () => {
  const { user, role } = useAuth();
  const queryClient = useQueryClient();
  const [pendingChanges, setPendingChanges] = useState<Record<string, ModuleTier>>({});

  const isAdmin = role === 'admin';

  const { data: flags = [], isLoading } = useQuery({
    queryKey: ['admin-tenant-tiers'],
    queryFn: async () => {
      const { data: ffData, error: ffError } = await (supabase as any)
        .from('emp_feature_flags')
        .select('id, cluster_id, modulo, tipo_modulo, ativo, config')
        .order('tipo_modulo', { ascending: true })
        .order('created_at', { ascending: true });

      if (ffError) throw ffError;

      const clusterIds = [...new Set((ffData || []).map((f: any) => f.cluster_id))];
      let clusterMap: Record<string, string> = {};
      if (clusterIds.length > 0) {
        const { data: clusters } = await supabase
          .from('clusters')
          .select('id, name')
          .in('id', clusterIds as string[]);
        clusterMap = (clusters || []).reduce((acc: any, c: any) => {
          acc[c.id] = c.name;
          return acc;
        }, {});
      }

      return (ffData || []).map((f: any) => ({
        ...f,
        cluster_name: clusterMap[f.cluster_id] || f.cluster_id.slice(0, 8) + '…',
      })) as FlagRow[];
    },
    enabled: isAdmin,
  });

  // Separate tier flags from addon flags
  const tierFlags = flags.filter(f => f.tipo_modulo === 'tier');
  const addonFlags = flags.filter(f => f.tipo_modulo === 'addon');

  // Group addon flags by cluster
  const addonsByCluster = addonFlags.reduce<Record<string, FlagRow[]>>((acc, f) => {
    if (!acc[f.cluster_id]) acc[f.cluster_id] = [];
    acc[f.cluster_id].push(f);
    return acc;
  }, {});

  const mutation = useMutation({
    mutationFn: async ({ flagId, clusterId, newTier, oldTier }: {
      flagId: string; clusterId: string; newTier: ModuleTier; oldTier: ModuleTier;
    }) => {
      const flag = flags.find(f => f.id === flagId);
      const currentConfig = flag?.config || {};
      const updatedConfig = { ...currentConfig, tenant_tier: newTier };

      const { error: updateError } = await (supabase as any)
        .from('emp_feature_flags')
        .update({ config: updatedConfig, updated_at: new Date().toISOString() })
        .eq('id', flagId);

      if (updateError) throw updateError;

      const { error: auditError } = await (supabase as any)
        .from('emp_auditoria')
        .insert({
          user_id: user?.id,
          entidade: 'emp_feature_flags',
          entidade_id: flagId,
          acao: 'alterar_tier',
          dados_resumo: {
            cluster_id: clusterId,
            tier_anterior: oldTier,
            tier_novo: newTier,
            alterado_em: new Date().toISOString(),
          },
        });

      if (auditError) {
        console.error('Audit log enrichment failed (trigger still recorded):', auditError);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-tiers'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-tier'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-addons'] });
      setPendingChanges(prev => {
        const next = { ...prev };
        delete next[variables.flagId];
        return next;
      });
      toast.success(`Tier alterado para "${TIER_LABELS[variables.newTier].label}" com sucesso.`);
    },
    onError: (error: any) => {
      toast.error(`Erro ao alterar tier: ${error.message}`);
    },
  });

  const addonMutation = useMutation({
    mutationFn: async ({ flagId, clusterId, addonId, newState }: {
      flagId: string | null; clusterId: string; addonId: string; newState: boolean;
    }) => {
      if (flagId) {
        // Update existing addon flag
        const { error } = await (supabase as any)
          .from('emp_feature_flags')
          .update({ ativo: newState, updated_at: new Date().toISOString() })
          .eq('id', flagId);
        if (error) throw error;
      } else {
        // Create new addon flag
        const addonDef = ADDON_DEFINITIONS.find(a => a.id === addonId);
        const { error } = await (supabase as any)
          .from('emp_feature_flags')
          .insert({
            cluster_id: clusterId,
            modulo: addonId,
            tipo_modulo: 'addon',
            ativo: newState,
            config: {
              addon_name: addonDef?.name || addonId,
              addon_desc: addonDef?.description || '',
            },
            ativado_por: user?.id,
            ativado_em: new Date().toISOString(),
          });
        if (error) throw error;
      }

      // Audit
      await (supabase as any)
        .from('emp_auditoria')
        .insert({
          user_id: user?.id,
          entidade: 'emp_feature_flags',
          entidade_id: flagId || 'new',
          acao: newState ? 'ativar_addon' : 'desativar_addon',
          dados_resumo: {
            cluster_id: clusterId,
            addon_id: addonId,
            estado: newState ? 'ativo' : 'inativo',
            alterado_em: new Date().toISOString(),
          },
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-tiers'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-addons'] });
      toast.success('Addon atualizado com sucesso.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar addon: ${error.message}`);
    },
  });

  const getCurrentTier = (flag: FlagRow): ModuleTier => {
    const cfg = flag.config || {};
    const tier = cfg.tenant_tier;
    if (tier && ['basico', 'profissional', 'enterprise'].includes(tier)) {
      return tier as ModuleTier;
    }
    return 'enterprise';
  };

  const handleTierChange = (flagId: string, newTier: ModuleTier) => {
    const flag = flags.find(f => f.id === flagId);
    if (!flag) return;
    const current = getCurrentTier(flag);
    if (newTier === current) {
      setPendingChanges(prev => {
        const next = { ...prev };
        delete next[flagId];
        return next;
      });
    } else {
      setPendingChanges(prev => ({ ...prev, [flagId]: newTier }));
    }
  };

  const handleSave = (flagId: string) => {
    const flag = flags.find(f => f.id === flagId);
    if (!flag) return;
    const newTier = pendingChanges[flagId];
    if (!newTier) return;
    const oldTier = getCurrentTier(flag);
    mutation.mutate({ flagId, clusterId: flag.cluster_id, newTier, oldTier });
  };

  const handleAddonToggle = (clusterId: string, addonId: string, currentState: boolean) => {
    const existing = addonFlags.find(f => f.cluster_id === clusterId && f.modulo === addonId);
    addonMutation.mutate({
      flagId: existing?.id || null,
      clusterId,
      addonId,
      newState: !currentState,
    });
  };

  // Guard AFTER all hooks
  if (!isAdmin) return null;

  if (isLoading) {
    return (
      <Card>
        <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
        <CardContent className="space-y-3">
          {[1, 2].map(i => <Skeleton key={i} className="h-12 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  // Get unique cluster IDs from tier flags
  const clusterIds = [...new Set(tierFlags.map(f => f.cluster_id))];

  return (
    <div className="space-y-6">
      {/* Tier Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Gerenciamento de Tier por Cluster
          </CardTitle>
          <CardDescription>
            Define o nível de acesso (Básico, Profissional, Enterprise) para cada cluster.
            Alterações são registradas em auditoria.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tierFlags.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Nenhum cluster com feature flag configurado.
            </p>
          ) : (
            <div className="space-y-3">
              {tierFlags.map(flag => {
                const currentTier = getCurrentTier(flag);
                const pending = pendingChanges[flag.id];
                const hasPending = !!pending && pending !== currentTier;

                return (
                  <div
                    key={flag.id}
                    className="flex items-center justify-between gap-4 rounded-lg border p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{flag.cluster_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={TIER_LABELS[currentTier].color}>
                          {TIER_LABELS[currentTier].label}
                        </Badge>
                        {!flag.ativo && (
                          <Badge variant="secondary" className="text-xs">Inativo</Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Select
                        value={pending || currentTier}
                        onValueChange={(v) => handleTierChange(flag.id, v as ModuleTier)}
                      >
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basico">Básico</SelectItem>
                          <SelectItem value="profissional">Profissional</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        size="sm"
                        disabled={!hasPending || mutation.isPending}
                        onClick={() => handleSave(flag.id)}
                      >
                        {mutation.isPending ? (
                          <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                        ) : hasPending ? (
                          <><Save className="h-3.5 w-3.5 mr-1" /> Salvar</>
                        ) : (
                          <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> OK</>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Addon Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plug className="h-4 w-4" />
            Módulos Avulsos (Addons)
          </CardTitle>
          <CardDescription>
            Ative ou desative módulos complementares individualmente por cluster.
            Addons são independentes do tier contratado.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {clusterIds.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Nenhum cluster configurado.
            </p>
          ) : (
            <div className="space-y-4">
              {clusterIds.map(clusterId => {
                const tierFlag = tierFlags.find(f => f.cluster_id === clusterId);
                const clusterName = tierFlag?.cluster_name || clusterId.slice(0, 8) + '…';
                const clusterAddons = addonsByCluster[clusterId] || [];

                return (
                  <div key={clusterId} className="rounded-lg border p-4 space-y-3">
                    <p className="text-sm font-medium">{clusterName}</p>
                    <div className="space-y-2">
                      {ADDON_DEFINITIONS.map(addon => {
                        const existingFlag = clusterAddons.find(f => f.modulo === addon.id);
                        const isActive = existingFlag?.ativo ?? false;
                        const AddonIcon = ADDON_ICONS[addon.id] || Plug;

                        return (
                          <div
                            key={addon.id}
                            className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <AddonIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{addon.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{addon.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={isActive
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
                                  : 'bg-muted text-muted-foreground'
                                }
                              >
                                {isActive ? 'Ativo' : 'Inativo'}
                              </Badge>
                              <Switch
                                checked={isActive}
                                onCheckedChange={() => handleAddonToggle(clusterId, addon.id, isActive)}
                                disabled={addonMutation.isPending}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
